/* =============================================================
   Product reviews — standard (card) and expanded (dialog) views.

   Three jobs, all of them measurement-driven and therefore re-run
   on resize and once the webfont has swapped in:

     1. Badges: only the pills that fit on the first line stay; the
        rest collapse into a "+N" pill that opens the expanded view.
     2. "Read more": shown only when the copy is actually clipped by
        the fixed-height card.
     3. Expanded view: one <dialog> per section, populated from the
        clicked card's <template>. Two navigation systems that must not
        be conflated: the arrows over the media cycle through the open
        review's photos and videos, wrapping from the last back to the
        first and never disabling; the band along the bottom steps
        through the reviews in section order, is not infinite, and
        disables its arrow at the first and the last review.
   ============================================================= */
(function () {
  'use strict';

  var RESIZE_DEBOUNCE_MS = 150;
  // How far a touch must travel before it is treated as a horizontal carousel
  // drag rather than a vertical page scroll.
  var SWIPE_AXIS_LOCK_PX = 8;
  // Fraction of the stage width a drag must cover to change slide.
  var SWIPE_THRESHOLD_RATIO = 0.15;
  // Must outlast the dialog's fade-out in section-collection-reviews.css.
  var CLOSE_ANIMATION_MS = 260;

  /* ─────────────────────────────────────────────────────────────
     Slider
     ───────────────────────────────────────────────────────────── */
  function setupSlider(root) {
    var el = root.querySelector('.reviews__splide');
    if (!el || el._splideReady || !window.Splide) return null;
    el._splideReady = true;

    var splide = new Splide(el).mount();

    var prevBtns = root.querySelectorAll('.reviews__nav-btn--prev');
    var nextBtns = root.querySelectorAll('.reviews__nav-btn--next');

    prevBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { splide.go('<'); });
    });
    nextBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { splide.go('>'); });
    });

    function getPerPage() {
      var track = el.querySelector('.splide__track');
      var slide = el.querySelector('.splide__slide');
      if (!track || !slide || slide.offsetWidth <= 0) return 1;
      var gap = parseFloat(window.getComputedStyle(el.querySelector('.splide__list')).gap) || 0;
      return Math.max(1, Math.floor((track.offsetWidth + gap) / (slide.offsetWidth + gap)));
    }

    function updateState() {
      var index = splide.index;
      var end = Math.max(0, splide.length - getPerPage());
      var isFirst = index === 0;
      var isLast = index >= end;
      prevBtns.forEach(function (btn) { btn.classList.toggle('reviews__nav-btn--disabled', isFirst); });
      nextBtns.forEach(function (btn) { btn.classList.toggle('reviews__nav-btn--disabled', isLast); });
    }

    splide.on('mounted moved resized', updateState);
    // Splide writes the slide width on mount, which changes how many badge
    // pills fit on the first line and whether the copy overflows.
    splide.on('mounted resized', function () {
      if (typeof root._reviewsMeasure === 'function') root._reviewsMeasure();
    });
    updateState();

    return splide;
  }

  /* ─────────────────────────────────────────────────────────────
     Badges — keep the first line, collapse the rest into "+N"
     ───────────────────────────────────────────────────────────── */
  function wrapsToSecondLine(nodes) {
    var top = null;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.hidden) continue;
      if (top === null) {
        top = el.offsetTop;
        continue;
      }
      // 1px of slack: sub-pixel pill heights must not read as a new line.
      if (el.offsetTop > top + 1) return true;
    }
    return false;
  }

  function fitBadges(card) {
    var list = card.querySelector('.reviews__card-head [data-review-badges]');
    if (!list) return;

    var items = Array.prototype.slice.call(list.querySelectorAll('[data-badge]'));
    var more = list.querySelector('[data-badge-more]');
    if (!items.length) return;

    items.forEach(function (el) { el.hidden = false; });
    if (more) more.hidden = true;

    var nodes = items.concat(more ? [more] : []);
    if (!wrapsToSecondLine(nodes) || !more) return;

    more.hidden = false;
    var visible = items.length;
    while (visible > 0) {
      visible -= 1;
      items[visible].hidden = true;
      more.textContent = '+' + (items.length - visible);
      if (!wrapsToSecondLine(nodes)) break;
    }
  }

  /* ─────────────────────────────────────────────────────────────
     "Read more" — only when the copy overflows the card
     ───────────────────────────────────────────────────────────── */
  function updateClamp(card) {
    var wrap = card.querySelector('.reviews__text-wrap');
    if (!wrap) return;
    card.classList.toggle('is-clamped', wrap.scrollHeight - wrap.clientHeight > 1);
  }

  /* ─────────────────────────────────────────────────────────────
     Expanded view
     ───────────────────────────────────────────────────────────── */
  function setupModal(root, scope) {
    var dialog = scope.querySelector('[data-reviews-modal]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    var body = dialog.querySelector('[data-reviews-modal-body]');
    var reviewPrev = dialog.querySelector('[data-review-prev]');
    var reviewNext = dialog.querySelector('[data-review-next]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-review-card]'));

    var currentCard = null;
    var currentIndex = 0;
    var slides = [];
    var thumbs = [];
    var track = null;
    var stage = null;
    var teardownTimer = null;

    function pauseVideos(container) {
      container.querySelectorAll('video').forEach(function (video) {
        if (!video.paused) video.pause();
      });
    }

    // Template images are lazy, and every slide past the first is translated out
    // of view, so its photo does not start downloading until the reader arrives.
    // The slide shows the media's black background until it lands, and the
    // repaint when it does is jarring. Warm the neighbours instead.
    function warmNeighbours() {
      for (var i = currentIndex - 1; i <= currentIndex + 1; i += 1) {
        if (i < 0 || i >= slides.length) continue;
        var image = slides[i].querySelector('img[loading="lazy"]');
        if (image) image.loading = 'eager';
      }
    }

    // Every write we make to `muted` records what it set, so the `volumechange`
    // it fires is not mistaken for the reader reaching for the mute control.
    function setMuted(video, value) {
      video._rvExpectedMuted = value;
      video.muted = value;
    }

    // Videos play with sound. The reader only ever reaches one by clicking — the
    // card, a thumbnail, an arrow, the review nav — and this runs inside that
    // click, so the user activation a browser wants before it allows sound is
    // present. Where one refuses anyway, fall back to a muted play rather than
    // leaving the reader looking at a still frame. A mute the reader set
    // themselves outlives both paths.
    function playVideo(video) {
      video._rvOwnPlay = true;
      setMuted(video, video._rvUserMuted === true);

      var started = video.play();
      if (!started || typeof started.catch !== 'function') return;

      started.catch(function () {
        if (video.muted) return; // already silent: the refusal was not about sound
        video._rvOwnPlay = true;
        setMuted(video, true);
        var retry = video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(function () {});
      });
    }

    // Bound on the clones inside the open dialog, which are new on every open.
    function bindVideoAudio(video) {
      video.addEventListener('volumechange', function () {
        // Only a change we did not make is the reader's.
        if (video.muted === video._rvExpectedMuted) return;
        video._rvUserMuted = video.muted;
        video._rvExpectedMuted = video.muted;
      });

      video.addEventListener('play', function () {
        if (video._rvOwnPlay) {
          video._rvOwnPlay = false;
          return;
        }
        // A play the reader pressed carries a gesture of its own, so sound is
        // allowed here even where the automatic attempt above was refused.
        if (!video._rvUserMuted && video.muted) setMuted(video, false);
      });
    }

    // The bottom band walks every review in section order — text-only ones
    // included — and is the only navigation that ever disables.
    function updateReviewNav() {
      var i = cards.indexOf(currentCard);
      if (reviewPrev) reviewPrev.disabled = i <= 0;
      if (reviewNext) reviewNext.disabled = i === -1 || i >= cards.length - 1;
    }

    function openAdjacentReview(direction) {
      var i = cards.indexOf(currentCard);
      if (i === -1) return;
      var target = cards[i + direction];
      if (target) open(target, 0);
    }

    // `animate: false` jumps without a transition — used when the dialog is
    // freshly populated, where sliding in from item 0 would be meaningless.
    function setIndex(index, animate) {
      if (!slides.length) return;
      var last = slides.length - 1;
      currentIndex = Math.min(Math.max(index, 0), last);

      if (animate === false && track) track.style.transition = 'none';
      if (track) track.style.transform = 'translateX(' + currentIndex * -100 + '%)';
      // Mobile only: the stage takes the shape of the asset on screen, so no
      // photo is letterboxed by a taller sibling. The stage is a fixed size
      // from 768px up and ignores this. Deliberately not transitioned — an
      // animated height slides the arrows out from under a waiting thumb.
      var ratio = slides[currentIndex].dataset.ratio;
      if (stage && ratio) stage.style.setProperty('--rv-stage-ratio', ratio);
      if (animate === false && track) {
        void track.offsetWidth; // flush before re-arming
        track.style.transition = '';
      }

      slides.forEach(function (slide, i) {
        var isActive = i === currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        var video = slide.querySelector('video');
        if (!video) return;
        if (isActive) playVideo(video);
        else if (!video.paused) video.pause();
      });

      warmNeighbours();

      thumbs.forEach(function (thumb, i) {
        var isActive = i === currentIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    // The bottom fade means "there is more copy below", so it goes away once
    // the reader reaches the end (and never shows when nothing scrolls).
    // Must run with the dialog already open: while it is closed the panel is
    // `display: none`, every measurement reads 0, and the fade would be hidden
    // until the first scroll re-measured it.
    function bindTextFade() {
      var text = body.querySelector('[data-modal-text]');
      var wrap = text && text.closest('.rv-modal__textwrap');
      if (!text || !wrap) return;

      function update() {
        var overflow = text.scrollHeight - text.clientHeight;
        var atEnd = overflow <= 1 || text.scrollTop >= overflow - 1;
        wrap.classList.toggle('is-end', atEnd);
      }

      text.addEventListener('scroll', update, { passive: true });
      update();
      // Re-measure once the webfont has swapped in and reflowed the copy.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    }


    // Touch drag on the media. Re-bound on every open because the stage node is
    // replaced each time the panel is repopulated, so the listeners go with it.
    function enableSwipe() {
      if (!stage) return;

      var startX = 0;
      var startY = 0;
      var width = 0;
      var tracking = false;
      var horizontal = false;

      function release() {
        tracking = false;
        horizontal = false;
        if (track) track.style.transition = '';
      }

      stage.addEventListener('pointerdown', function (event) {
        // Mouse users have the arrows; dragging with a mouse would fight text
        // selection and feel like a stuck click.
        if (event.pointerType === 'mouse' || slides.length < 2) return;
        tracking = true;
        horizontal = false;
        startX = event.clientX;
        startY = event.clientY;
        width = stage.getBoundingClientRect().width;
      });

      stage.addEventListener('pointermove', function (event) {
        if (!tracking) return;
        var dx = event.clientX - startX;
        var dy = event.clientY - startY;

        if (!horizontal) {
          if (Math.abs(dx) < SWIPE_AXIS_LOCK_PX && Math.abs(dy) < SWIPE_AXIS_LOCK_PX) return;
          // Vertical intent belongs to the page, so hand the gesture back.
          if (Math.abs(dy) >= Math.abs(dx)) {
            tracking = false;
            return;
          }
          horizontal = true;
          if (track) track.style.transition = 'none';
        }

        // Resist past the ends: the track has no clone slides, so an unresisted
        // drag past the last item would expose the empty stage. Releasing past
        // the threshold still wraps.
        var atStart = currentIndex === 0 && dx > 0;
        var atEnd = currentIndex === slides.length - 1 && dx < 0;
        var travel = atStart || atEnd ? dx * 0.25 : dx;
        if (track) track.style.transform = 'translateX(' + (currentIndex * -width + travel) + 'px)';
      });

      stage.addEventListener('pointerup', function (event) {
        // Bail before touching the carousel unless this really was a drag.
        // Anything else — a tap, a mouse release, a press on an arrow — has to
        // fall through untouched so the click handler can act on it once.
        if (!tracking) return;
        var wasDrag = horizontal;
        var dx = event.clientX - startX;
        release();
        if (!wasDrag || width <= 0) return;

        if (dx <= -SWIPE_THRESHOLD_RATIO * width) goNext();
        else if (dx >= SWIPE_THRESHOLD_RATIO * width) goPrev();
        else setIndex(currentIndex);
      });

      stage.addEventListener('pointercancel', function () {
        if (!tracking) return;
        var wasDrag = horizontal;
        release();
        if (wasDrag) setIndex(currentIndex);
      });
    }

    function open(card, index) {
      var template = card.querySelector('[data-review-template]');
      if (!template) return;

      clearTimeout(teardownTimer);
      pauseVideos(body);
      body.textContent = '';
      body.appendChild(template.content.cloneNode(true));

      currentCard = card;
      slides = Array.prototype.slice.call(body.querySelectorAll('[data-modal-slide]'));
      thumbs = Array.prototype.slice.call(body.querySelectorAll('[data-modal-thumb]'));
      track = body.querySelector('[data-modal-track]');
      stage = body.querySelector('[data-modal-stage]');
      body.querySelectorAll('video').forEach(bindVideoAudio);

      // Open before measuring — see bindTextFade.
      if (!dialog.open) dialog.showModal();

      bindTextFade();
      enableSwipe();
      setIndex(index, false);
      updateReviewNav();
    }

    // The media arrows are infinite within the review: past the last item the
    // next arrow returns to the first, and vice versa. The wrap is a cut rather
    // than a slide — without clone slides, animating it would sweep the whole
    // strip back across the stage.
    function goNext() {
      if (!slides.length) return;
      if (currentIndex < slides.length - 1) setIndex(currentIndex + 1);
      else setIndex(0, false);
    }

    function goPrev() {
      if (!slides.length) return;
      if (currentIndex > 0) setIndex(currentIndex - 1);
      else setIndex(slides.length - 1, false);
    }

    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-review-open]');
      if (!trigger || !root.contains(trigger)) return;
      var card = trigger.closest('[data-review-card]');
      if (!card) return;
      open(card, parseInt(trigger.dataset.mediaIndex, 10) || 0);
    });

    dialog.addEventListener('click', function (event) {
      if (event.target.closest('[data-modal-close]')) {
        dialog.close();
        return;
      }
      if (event.target.closest('[data-modal-prev]')) {
        goPrev();
        return;
      }
      if (event.target.closest('[data-modal-next]')) {
        goNext();
        return;
      }
      if (event.target.closest('[data-review-prev]')) {
        openAdjacentReview(-1);
        return;
      }
      if (event.target.closest('[data-review-next]')) {
        openAdjacentReview(1);
        return;
      }
      var thumb = event.target.closest('[data-modal-thumb]');
      if (thumb) {
        setIndex(parseInt(thumb.dataset.mediaIndex, 10) || 0);
        return;
      }
      // Anything outside the white panel is the backdrop.
      if (!event.target.closest('[data-reviews-modal-panel]')) dialog.close();
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    });

    // `close` also fires for Escape and the backdrop, so the teardown lives
    // here. Emptying the panel is deferred past the fade-out, otherwise the
    // dialog would visibly go blank before it finishes animating away.
    dialog.addEventListener('close', function () {
      pauseVideos(body);
      clearTimeout(teardownTimer);
      teardownTimer = setTimeout(function () {
        if (dialog.open) return; // reopened mid-fade
        body.textContent = '';
        currentCard = null;
        slides = [];
        thumbs = [];
        track = null;
        stage = null;
      }, CLOSE_ANIMATION_MS);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Wiring
     ───────────────────────────────────────────────────────────── */
  function setupSection(root) {
    if (root._reviewsReady) return;
    root._reviewsReady = true;

    var scope = root.closest('.shopify-section') || root.parentNode || document;
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-review-card]'));

    function measure() {
      cards.forEach(function (card) {
        fitBadges(card);
        updateClamp(card);
      });
    }

    setupModal(root, scope);
    root._reviewsMeasure = measure;
    measure();

    // Atten New swaps in after first paint and changes every text width.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, RESIZE_DEBOUNCE_MS);
    });
  }

  // The cards, the badge fitting and the expanded view do not depend on
  // Splide, so they are wired up straight away; only the slider waits for the
  // library. A failed Splide load then costs the arrows, not the whole section.
  function init() {
    document.querySelectorAll('[data-reviews-root]').forEach(setupSection);
    if (window.Splide) mountSliders();
    else document.addEventListener('splide:ready', mountSliders, { once: true });
  }

  function mountSliders() {
    document.querySelectorAll('[data-reviews-root]').forEach(setupSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('[data-reviews-root]')) init();
  });
})();
